// Spec detail — a node-graph view of the section dependency graph. Read
// only: humans inspect here, agents edit via spec_* tools.
import { useClient } from '@cortex/react-query';
import type { SpecWithSections } from '@cortex/types';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';

import { BackButton } from '../../../../components/common/BackButton';
import { PageHeader } from '../../../../components/common/PageHeader';
import { Spinner } from '../../../../components/common/Spinner';
import { routes } from '../../../../lib/constants';

import { SpecGraphView } from './-graph-view';

export const Route = createFileRoute('/agents/orchestration/specs/$specId')({
  component: SpecPage,
});

function SpecPage() {
  const { specId } = Route.useParams();
  const client = useClient();
  const base = `v1/specs/${specId}`;

  const specQuery = useQuery({
    queryKey: ['specs', specId],
    queryFn: () => client.http.get(base).json<SpecWithSections>(),
  });

  const spec = specQuery.data;
  const sections = useMemo(
    () => [...(spec?.sections ?? [])].sort((a, b) => a.position - b.position),
    [spec?.sections],
  );
  const dependencies = useMemo(() => spec?.dependencies ?? [], [spec?.dependencies]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <PageHeader
        title={
          spec ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 'var(--fs-lg)' }}>{spec.name}</span>
            </span>
          ) : (
            'Spec'
          )
        }
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <BackButton to={routes.ORCHESTRATION_SPECS} label="Mission Specs" />
            {spec && spec.agentIds.length > 0 && (
              <span className="t-xs t-muted">
                bound to {spec.agentIds.length} agent{spec.agentIds.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
        }
      />

      {specQuery.isLoading && <Spinner />}

      {spec && (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            padding: '0 16px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {spec.description && (
            <p className="t-sm t-muted" style={{ margin: '4px 0 0' }}>
              {spec.description}
            </p>
          )}
          {sections.length === 0 ? (
            <div className="card" style={{ padding: 24, textAlign: 'center' }}>
              <p className="t-sm t-muted" style={{ margin: 0 }}>
                No sections yet.
              </p>
            </div>
          ) : (
            <SpecGraphView sections={sections} dependencies={dependencies} />
          )}
        </div>
      )}
    </div>
  );
}
